import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import {
  DEFAULT_THEME_ID,
  DesignTheme,
  ThemeId,
  ThemeMode,
  getTheme,
  hasTheme,
  listThemes,
} from '../tokens'
import { applyCssVariables, createShellTheme } from '../mui/createShellTheme'

const STORAGE_KEY = 'ds-theme-id'

export interface DesignSystemContextValue {
  theme: DesignTheme
  themeId: ThemeId
  mode: ThemeMode
  themes: DesignTheme[]
  setThemeId: (id: ThemeId) => void
  toggleMode: () => void
}

export const DesignSystemContext = createContext<DesignSystemContextValue | null>(null)

function resolveInitialId(preferred?: ThemeId): ThemeId {
  if (preferred && hasTheme(preferred)) return preferred
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && hasTheme(stored)) return stored
  } catch {
    /* ignore */
  }
  return DEFAULT_THEME_ID
}

export interface DesignSystemProviderProps {
  children: React.ReactNode
  /** Override default theme id (e.g. from a future brand/theme pack) */
  defaultThemeId?: ThemeId
}

export function DesignSystemProvider({ children, defaultThemeId }: DesignSystemProviderProps) {
  const [themeId, setThemeIdState] = useState<ThemeId>(() => resolveInitialId(defaultThemeId))

  const theme = useMemo(() => getTheme(themeId), [themeId])
  const muiTheme = useMemo(() => createShellTheme(theme), [theme])

  useEffect(() => {
    applyCssVariables(theme)
  }, [theme])

  const setThemeId = useCallback((id: ThemeId) => {
    setThemeIdState(id)
    try {
      localStorage.setItem(STORAGE_KEY, id)
    } catch {
      /* ignore */
    }
  }, [])

  const toggleMode = useCallback(() => {
    if (theme.mode === 'light') {
      const darkId = theme.id.endsWith('-dark') ? theme.id : `${theme.id}-dark`
      setThemeId(hasTheme(darkId) ? darkId : 'default-dark')
    } else {
      const lightId = theme.id.replace(/-dark$/, '') || DEFAULT_THEME_ID
      setThemeId(hasTheme(lightId) ? lightId : DEFAULT_THEME_ID)
    }
  }, [theme, setThemeId])

  const value = useMemo<DesignSystemContextValue>(
    () => ({
      theme,
      themeId,
      mode: theme.mode,
      themes: listThemes(),
      setThemeId,
      toggleMode,
    }),
    [theme, themeId, setThemeId, toggleMode],
  )

  return (
    <DesignSystemContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </DesignSystemContext.Provider>
  )
}
